import { getTheme } from '@fluentui/react';
import { useContext } from 'react';

import AppContext from 'App/AppContext';
import { IPFontIcon, IPSeparator, IPText } from 'baseComponents';
import { IPPersona, IPPersonaSize } from 'baseComponents/Persona/IPPersona';
import { IPStack } from 'baseComponents/Stack/IPStack';
import CheckListProgress from 'components/Checklist/ChecklistProgress';
import { RecurringPatternSummaryStyle, RecurringSummary } from 'components/RecurringPattern/RecurringSummary';
import { KeyValueTags } from 'components/Tags/KeyValueTag';
import {
  getFontIconColorPrimary,
  globalFontBoldWeight,
  globalStackItemStylesCard,
  globalStackStylesCard,
  globalStackTokensGapCard,
  globalStackTokensGapSmall,
  globalTextStylesCard,
  globalTextStylesCardRed,
} from 'globalStyles';
import { toLocaleDateShortNoYear } from 'utils/datetime';
import { ICardTaskProps } from './KanbanCard';

const KanbanCardTaskGeneric = (props: ICardTaskProps) => {
  const appContext = useContext(AppContext);
  const theme = getTheme();

  return (
    <div
      onClick={() => {
        if (props.onClick) props.onClick(props.task);
      }}
    >
      <IPStack styles={globalStackStylesCard} tokens={globalStackTokensGapCard}>
        <IPStack.Item styles={globalStackItemStylesCard}>
          <IPText
            block
            styles={{
              root: { fontWeight: globalFontBoldWeight, paddingTop: 0, paddingBottom: 0, wordBreak: 'break-word' },
            }}
          >
            {props.task.name}
          </IPText>
        </IPStack.Item>
        <IPStack.Item>
          <CheckListProgress
            compact
            totalChecks={props.task.checkList.items.length}
            successChecks={props.task.checkList.getSuccessfulCount()}
            failedChecks={props.task.checkList.getFailedCount()}
          ></CheckListProgress>
        </IPStack.Item>
        {props.task.tagIds && props.task.tagIds.length > 0 && (
          <IPStack.Item styles={globalStackItemStylesCard}>
            <IPSeparator styles={{ root: { height: 1 } }}></IPSeparator>
            <KeyValueTags
              compact
              tagWidth={150}
              tags={appContext.globalDataCache.tags.getItemsForId(props.task.tagIds)}
            />
          </IPStack.Item>
        )}
        <IPStack.Item styles={globalStackItemStylesCard}>
          <IPSeparator styles={{ root: { height: 1 } }}></IPSeparator>
          <IPStack horizontal tokens={globalStackTokensGapSmall} verticalAlign="center">
            <IPStack.Item>
              <IPText block variant="small" styles={globalTextStylesCard}>
                {toLocaleDateShortNoYear(props.task.startDateTime)}
              </IPText>
            </IPStack.Item>
            <IPText>-</IPText>
            <IPStack.Item>
              <IPText
                block
                variant="small"
                styles={
                  props.task.getMinutesOverDeadline(props.task.completed || new Date(), appContext.globalDataCache) > 0
                    ? globalTextStylesCardRed
                    : globalTextStylesCard
                }
              >
                {toLocaleDateShortNoYear(props.task.getDeadline(appContext.globalDataCache))}
              </IPText>
            </IPStack.Item>
            {props.task.recurringPattern.isActive && (
              <IPStack.Item>
                <RecurringSummary
                  pattern={props.task.recurringPattern}
                  summaryStyle={RecurringPatternSummaryStyle.icon}
                />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
        <IPSeparator styles={{ root: { height: 1 } }}></IPSeparator>
        <IPStack horizontal horizontalAlign="space-between" verticalAlign="center">
          <IPPersona
            text={props.task.user ? props.task.user.name : undefined}
            imageUrl={appContext.globalDataCache.users.getPhotoURLObj(props.task.userId)}
            size={IPPersonaSize.size24}
            hidePersonaDetails={false}
          />
          {props.task.isScheduled() && (
            <IPFontIcon iconName="Calendar" style={{ color: getFontIconColorPrimary(appContext.useDarkMode, theme) }} />
          )}
        </IPStack>
      </IPStack>
    </div>
  );
};

export default KanbanCardTaskGeneric;
